import { OPTIMIZELY_EXPERIMENTS, type OpzlyExperimentName } from '../config';
import { type CachedExperiments } from '../types';

/**
 * By providing the experiment names you are interested in and the cached experiments dictionary
 * this helper will return the decision for all experiments and provide a fallback decision for
 * the requested experiment names if no cached experiments is supplied.
 *
 * This helper is already used by the useExperimentsV2 hook under the hood so ordinarily will not
 * be needed in client components.
 *
 * If you need to access an experiment decision server-side then use SERVERSIDE_getExperimentsDecision instead.
 */
export function getExperimentsDecision(
  experimentNames: OpzlyExperimentName[],
  cachedExperiments?: CachedExperiments
) {
  /**
   * We build this object of fallback experiments from the passed
   * list to return if the cachedExperiments variable is undefined.
   */
  const fallbackExps = experimentNames.reduce<CachedExperiments>(
    (acc, expName) => {
      acc[expName] = {
        decision: OPTIMIZELY_EXPERIMENTS[expName]?.control,
        bucketed: false,
      };
      return acc;
    },
    {}
  );

  /**
   * We merge the fallback exps which is built from the list passed into the helper
   * with the cached experiments to ensure that for any experiments passed into this
   * helper which don't exist in the cache we return the control variants so rendering
   * is unaffected.
   */
  const experimentsToReturn = { ...fallbackExps, ...cachedExperiments };

  /**
   * Transforms result into object of { experiment_name: "control|variant|etc" }
   * for easy consumption by consumers
   */
  return Object.keys(experimentsToReturn).reduce<Record<string, string>>(
    (acc, exp) => {
      acc[exp] = experimentsToReturn[exp].decision;
      return acc;
    },
    {}
  );
}
